import React from 'react';
import { routes } from '~/marketing/routes';
import { withMarketingLayout } from '~/marketing/MarketingLayout';
import { AEO_DATA } from '~/marketing/marketingData';
import { Post, Section, P, UL, PostLink, BENCHMARK, Sources, Cta } from '~/marketing/Post';
import { said, by, listingShare, one, word } from '~/marketing/research/figures';

/* How to appear in the sources AI assistants read. Approved 2026-10-13 from
   `docs/blog/drafts.md`, post 4.

   **It names no directory**: naming a site makes a claim about a named
   business, which the copy rules treat as Class 2. The family law and
   chiropractic figures are the highest and lowest listing shares, which
   `benchmark_post_test` holds against the file.

   **It closes on the trial, not the free check**, because the free check's
   report lists no sources, and a plan's Overview does. And it says how often a
   plan asks, not how many AI assistants: ChatGPT and Claude are asked the
   who-to-call questions only (`quoted.engine_coverage`). Both 2026-10-14. */
export default function GetListed() {
  return (
    <Post path="/blog/get-listed-where-ai-assistants-look" lede={<>
      An AI assistant can only name a business it has read about. So the most practical part of <PostLink to="/blog/what-is-answer-engine-optimization">answer engine optimization</PostLink> is appearing in the sources it reads, and making sure they describe you correctly.
    </>}>
      <Section title="Find the sources for your industry and city">
        <P>
          The sites an AI assistant cites differ by industry. In <PostLink to={BENCHMARK}>our benchmark</PostLink>, places a business can be listed, reviewed or mentioned made up {one(listingShare(by.family_law))}% of citations in family law and {one(listingShare(by.chiropractic))}% in chiropractic.
        </P>
        <P>
          Start by finding the sites that appear for your own questions. Ask an AI assistant what your customers ask, in your city, and note the sources it cites. The sources change each time you ask and from one AI assistant to another, so a reliable list takes many answers, collected over weeks. A Spruce plan does that for you: every week it asks your questions three times each and lists the sources it finds, split into places that can list you and businesses’ own pages.
        </P>
      </Section>

      <Section title="Claim and complete your directory listings">
        <UL>
          <li>Claim your listing on every directory that appears for your questions.</li>
          <li>Use the same business name, address and phone number on each one.</li>
          <li>Choose the categories that match what customers ask for, and describe your services in plain words.</li>
          <li>Name the areas you serve, and keep your hours current.</li>
        </UL>
      </Section>

      <Section title="Ask for reviews where AI assistants look">
        <P>
          Review sites are among the sources AI assistants cite. Ask customers to review you on the review sites that appear for your questions, so each review is on a site AI assistants already cite.
        </P>
      </Section>

      <Section title="Get into the roundups">
        <P>
          “Best of” articles and local roundups are cited often. Find the ones that appear for your questions and read how each one chooses its businesses: some take submissions, some are updated once a year, and some are written by local publications. Give each the facts it needs to include you.
        </P>
      </Section>

      <Section title="Take part where people ask">
        <P>
          Forum threads are cited too. Where people in your area ask for recommendations, answer helpfully and honestly, and say who you are.
        </P>
      </Section>

      <Section title="Publish the page an answer would quote">
        <P>
          In our benchmark, {said.own} of citations went to businesses’ own websites. Write a page for each service you offer that answers what customers ask: what it costs, how long it takes, and what to look for. Say on it which areas you serve. A page that answers the question is the kind of page an AI assistant can cite.
        </P>
      </Section>

      <Section title="Keep your name consistent">
        <P>
          AI assistants write a business’s name in many ways. One form of your name, used everywhere, is easier to recognize. If customers also know you by another name, mention it on your website alongside your main one.
        </P>
      </Section>

      <Section title="Then measure">
        <P>
          Listings put you in the sources. Whether an AI assistant names you is something to <PostLink to="/blog/how-to-measure-ai-visibility">measure across repeated answers</PostLink>, week after week.
        </P>
      </Section>

      <Sources />
      <Cta heading={`See which sources AI assistants cite for your questions, free for ${word(AEO_DATA.trial.days)} days.`} label="Start free" href={routes.signUp} />

    </Post>
  );
}

GetListed.layout = withMarketingLayout;
